import type { BaseAction } from '@morpho-org/morpho-sdk'

import { wrappedBackedTokenAbi } from '@morpho-org/blue-sdk-viem'
import { type Address, encodeFunctionData, erc20Abi } from 'viem'

import type {
  BuiltTxRequirement,
  TransactionBuilder
} from '@/contexts/Transaction/TransactionV2.context'

import { mainnetAddresses } from '@/helpers/addresses'

// The wrap is not one of morpho-sdk's entity actions, so the final tx carries a descriptive
// non-SDK action type. `action` is metadata only (analytics label); the context reads to/value/data.
const WRAP_ACTION: BaseAction = { type: 'wrapLegacyMorpho', args: {} }

export interface WrapMorphoV2TransactionParams {
  amount: bigint
  userAddress: Address
  /** Wrapper allowance, used to decide whether an approval requirement is needed. */
  wrapperAllowance: bigint
}

export interface WrapMorphoV2Transaction {
  transaction: TransactionBuilder
  requirements: BuiltTxRequirement[]
}

/**
 * Builds the "wrap legacy MORPHO" transaction in the {@link TransactionBuilder} + `Requirement`
 * shapes the V2 transaction context expects, so the flow runs through `submitTransactions` +
 * `ConfirmDialogV2` like every other flow (no classic `setActiveBundle` / `ProcessingDialogClassic`).
 *
 * The flow is a plain approve → `depositFor` on the MORPHO wrapper: an ERC20 approval of
 * `morphoTokenWrapper` is added as a `BuiltTxRequirement` when `wrapperAllowance` covers less
 * than `amount`, and the final transaction calls `depositFor(userAddress, amount)` on the
 * wrapper.
 */
export function getWrapMorphoV2Transaction(
  params: WrapMorphoV2TransactionParams
): WrapMorphoV2Transaction {
  const { amount, userAddress, wrapperAllowance } = params

  const requirements: BuiltTxRequirement[] = []
  if (wrapperAllowance < amount) {
    requirements.push({
      to: mainnetAddresses.legacyMorphoToken,
      value: 0n,
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'approve',
        args: [mainnetAddresses.morphoTokenWrapper, amount]
      }),
      action: {
        type: 'erc20Approval',
        args: { spender: mainnetAddresses.morphoTokenWrapper, amount }
      }
    })
  }
  return {
    requirements,
    transaction: {
      buildTx: () => ({
        to: mainnetAddresses.morphoTokenWrapper,
        value: 0n,
        data: encodeFunctionData({
          abi: wrappedBackedTokenAbi,
          functionName: 'depositFor',
          args: [userAddress, amount]
        }),
        action: WRAP_ACTION
      })
    }
  }
}
